import { FormikHelpers } from "formik";
import { UserProfile } from "../api/profile";

export type UUID = string;

/**
 * example: "2021-04-13T08:45:18.901099Z", "2021-04-12T16:00:00"
 */
export type ISODate = string; // example: "2021-04-13T08:45:18.901099Z", "2021-04-12T16:00:00"

/**
 * example: "OU=Developer, O=Bob, L=New York, C=US"
 */
export type X500Name = string;

/**
 * example: "100000 SGD"
 */
export type AmountString = string;

export type LinearId = {
    externalId?: string;
    id: UUID;
};

export type Handler = {
    isSubmitting: boolean;
    handler: () => void;
};

export type StateAndRef<T> = {
    ref: string;
    state: {
        data: T;
    };
};

export type GenericState<PROPS, STATUS> = {
    createdAt: ISODate;
    linearId: LinearId;
    status: STATUS;
    props: PROPS;
    lastChangedTime: ISODate;
    comments: Comment[];
};
export type GenericBesuState<PROPS, STATUS> = PROPS & {
    createdAt: ISODate;
    updatedAt: ISODate;
    status: STATUS;
    commentCount: number;
    comments: Comment[];
};

export type Mutator<T> = (data?: T) => Promise<T | undefined>;

export type DataContext<T> = {
    data: T;
    mutate: Mutator<T>;
};

export type WorkflowResult<T> = {
    stateAndRefMap: T;
};

export type BankAccountDetails = {
    bankName: string;
    bankCode: string;
    bankAddress: string;
    country: string;
    accountName: string;
    accountNumber: string;
};

export type BeneficiaryDetails = {
    name: string;
    address: string;
    bankAccountDetails: BankAccountDetails;
};
export type Party = {
    name: string;
    party: X500Name;
    accountName: string;
};
export type PartyDetail = {
    party: X500Name;
    account: AccountInfo;
};

export type AccountInfo = {
    host: string;
    identifier: AccountInfoId;
    linearId: AccountInfoId;
    name: string;
    participants: string[],
};

export type AccountInfoId = {
    externalId?: string;
    id: string;
}

export type Contact = {
    company: string;
    email: string;
    contactPerson: string;
    phone: string;
    // contactId: string,
    // countryCode: string,
    // id: UUID,
    // position: string,
    // remark: string,
    // role: string
};

export type ContactResponse = {
    party: X500Name;
    type: string;
    name: string;
    contactPerson: string;
    contactEmail: string;
    contactNumber: string;
    location: string;
    accountName: string;
};

export type Director = {
    email: string;
    username: string;
    fullName: string;
};

export type Comment = {
    fullName: string;
    email: string;
    comment: string;
    resolved: boolean;
    created: ISODate;
};

export type FormSubmissionHandler<FormValues> = (
    value: FormValues,
    helpers: FormikHelpers<FormValues>,
) => void;

export type UploadResponse = {
    attId: string;
    size: number;
};
export type UploadedFile = {
    id: string;
    name: string;
    fileType?: string;
};
export type UploadedGCSFile = {
    documentId: string;
    documentHashHex: string;
    documentName: string;
};
export type UploadedFileWithSize = UploadedFile & {
    size?: number;
};
export type UploadedGCSFileWithSize = UploadedGCSFile & {
    size?: number;
};
export type UploadMeta = {
    contractorAccountName?: string;
    developerAccountName?: string;
};

export type FileUploadHandler = (
    file: File,
    meta?: UploadMeta,
) => Promise<UploadResponse>;
export type FileDeleteHandler = (hash: string) => Promise<unknown>;
export type HistoryItem<T> = {
    status: T;
    timestamp: ISODate;
};

export const MOCK_FILE_UPLOAD_HANDLER: FileUploadHandler = async (file) =>
    new Promise((resolve) =>
        setTimeout(
            () =>
                resolve({
                    attId: "some-id",
                    size: file.size,
                }),
            3000,
        ),
    );

export const MOCK_DEVELOPER: PartyDetail = {
    account: {
        host: "OU=Developer, O=Bob, L=New York, C=US",
        identifier: {
            id: "9bf629f8-0805-44e0-839d-628b2b4ea900",
        },
        linearId: {
            id: "9bf629f8-0805-44e0-839d-628b2b4ea900",
        },
        name: "CMFUatConAAcct003",
        participants: [
            "CN=Epsilon Service Provider HSBC, OU=Contractor, O=Epsilon Service Provider HSBC, L=Singapore, C=SG"
        ],
    },
    party: "net.corda.core.identity.AnonymousParty$Companion@3af52779",
};

export const MOCK_DEVELOPER_CONTACT: Contact = {
    company: "Western Developer Co. Ltd.",
    contactPerson: "some developer person",
    email: "some-email@western-dev.com",
    phone: "22334555",
};

export const MOCK_CONTRACTOR: PartyDetail[] = [{
    account: {
        host: "OU=Contractor, O=Alice, L=London, C=GB",
        identifier: {
            id: "9bf629f8-0805-44e0-839d-628b2b4ea900",
        },
        linearId: {
            id: "9bf629f8-0805-44e0-839d-628b2b4ea900",
        },
        name: "CMFUatConAAcct003",
        participants: [
            "CN=Epsilon Service Provider HSBC, OU=Contractor, O=Epsilon Service Provider HSBC, L=Singapore, C=SG"
        ],
    },
    party: "net.corda.core.identity.AnonymousParty$Companion@3af52779",
}];

export const MOCK_CONTRACT_CONTACT: Contact = {
    company: "Western Developer Co. Ltd.",
    contactPerson: "some contractor person",
    email: "some-email@lighten-con.com",
    phone: "94453434",
};

export const MOCK_CONTRACT_CONTACT2: Party = {
    name: MOCK_CONTRACTOR[0].account.name,
    party: MOCK_CONTRACTOR[0].party,
    accountName: MOCK_CONTRACTOR[0].account.name,
};

export const MOCK_ARCHITECT: PartyDetail = {
    account: {
        host: "OU=ARCHITECT, O=Alice, L=London, C=GB",
        identifier: {
            id: "9bf629f8-0805-44e0-839d-628b2b4ea900",
        },
        linearId: {
            id: "9bf629f8-0805-44e0-839d-628b2b4ea900",
        },
        name: "CMFUatConAAcct003",
        participants: [
            "CN=Epsilon Service Provider HSBC, OU=Contractor, O=Epsilon Service Provider HSBC, L=Singapore, C=SG"
        ],
    },
    party: "net.corda.core.identity.AnonymousParty$Companion@3af52779",
};

export const MOCK_ARCHITECT_CONTACT: Contact = {
    company: "Western Developer Co. Ltd.",
    contactPerson: "some architect person",
    email: "some-email@hailey-architect.com",
    phone: "34433222",
};

export const MOCK_DIRECTOR: Director = {
    username: "director",
    email: "some-email@western-dev.com",
    fullName: "some director",
};

export const MOCK_BANK: X500Name = "OU=Bank, O=Charlie, L=Singapore, C=SG";

export const MOCK_BENE_BANK_ACCOUNT: BeneficiaryDetails = {
    name: "Lighten Contractor Co. Ltd.",
    address: "688 Quarry Drive, Newvillem, Alabama",
    bankAccountDetails: {
        bankName: "The Hongkong and Shanghai Banking Corporation Limited",
        bankCode: "HSBCSGSGXXX",
        bankAddress:
            "Marina Bay Financial Centre Tower 2, 48-01, 10 Marina Boulevard",
        country: "Singapore",
        accountName: "Lighten Contractor Co. Ltd.",
        accountNumber: "595-799-6238",
    },
};

export const MOCK_BENE_BANK_ACCOUNT_LIST: BeneficiaryDetails[] = [
    MOCK_BENE_BANK_ACCOUNT,
    {
        ...MOCK_BENE_BANK_ACCOUNT,
        name: "Lighten Contractor Co. Ltd. 2",
        bankAccountDetails: {
            ...MOCK_BENE_BANK_ACCOUNT.bankAccountDetails,
            accountName: "Lighten Contractor Co. Ltd. 2",
            accountNumber: "234-666-2345",
        },
    },
];

export const MOCK_PAYER_BANK_ACCOUNT: BeneficiaryDetails = {
    name: "Western Developer Co. Ltd.",
    address: "535 Delaware Avenue, San Francisco",
    bankAccountDetails: {
        bankName: "The Hongkong and Shanghai Banking Corporation Limited",
        bankCode: "HSBCSGSGXXX",
        bankAddress:
            "Marina Bay Financial Centre Tower 2, 48-01, 10 Marina Boulevard",
        country: "Singapore",
        accountName: "Western Developer Co. Ltd.",
        accountNumber: "120-232-4533",
    },
};

export const MOCK_BANK_ACCOUNT: BankAccount = {
    id: 44,
    bankAccountHolderName: "John Doe",
    bankAccountNumber: "123312331233",
    swiftAddress: "ABCD",
    bankName: "The bank of bank bank",
    registeredAddress: "somewhere over the rainbow",
    currency: "HKD",
    userProfile: {
        id: 3,
        type: "DEVELOPER",
        name: "JAPURA DEVELOPMENT PTE LTD",
        location: "Singapore",
        contactPersonName: "Toh Ken Hooi",
        contactPersonNumber: "66039935",
        contactPersonEmail: "kenhooi.toh@ped.com.sg",
        accountName: "DemoDevAAcct001",
        wallet: "0x1234567890abcdef",
    },
};

export const MOCK_PAYER_BANK_ACCOUNT_LIST: BankAccount[] = [MOCK_BANK_ACCOUNT];

export const MOCK_BANK_ACCOUNT_LIST: BankAccount[] = [
    MOCK_BANK_ACCOUNT,
    MOCK_BANK_ACCOUNT,
];

export type ResponseWithPagination<T> = {
    content: T;
    pageable: {
        sort: {
            sorted: boolean;
            unsorted: boolean;
            empty: boolean;
        };
        pageSize: number;
        pageNumber: number;
        offset: number;
        paged: boolean;
        unpaged: boolean;
    };
    last: boolean;
    totalPages: number;
    totalElements: number;
    first: boolean;
    numberOfElements: number;
    size: number;
    number: number;
    sort: {
        sorted: boolean;
        unsorted: boolean;
        empty: boolean;
    };
    empty: boolean;
};

export type PaginationResponse<T> = {
    pageList: T;
    nrOfElements: number;
    page: number;
    pageSize: number;
    pageCount: number;
    firstPage: boolean;
    lastPage: boolean;
    sort: {
        property: string;
        ignoreCase: boolean;
        ascending: boolean;
        toggleAscendingOnProperty: boolean;
    };
};

export type Pagination<T> = {
    items: T;
    page: number;
    size: number;
    total: number;
};

export type BankAccount = {
    id?: number;
    bankAccountHolderName: string;
    bankAccountNumber: string;
    swiftAddress: string;
    bankName: string;
    registeredAddress: string;
    currency: string;
    userProfile: UserProfile;
    status?: BankStatus;
    tmpBankAccount?: TmpBankAccount;
    submitBy?: string;
    reviewBy?: string;
    reviewStatus?: BankReviewStatus;
    rejectReason?: string;
};

export type TmpBankAccount = {
    accountId: number;
    bankAccountHolderName: string;
    bankAccountNumber: string;
    swiftAddress: string;
    bankName: string;
    registeredAddress: string;
    currency: string;
}

export enum BankStatus {
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE",
    LOCKED = "LOCKED",
    PENDING_APPROVAL = "PENDING_APPROVAL",
    PENDING_EDIT_APPROVAL = "PENDING_EDIT_APPROVAL",
    DRAFT = "DRAFT",
}

export enum BankReviewStatus {
    APPROVED = "APPROVED",
    REJECTED = "REJECTED",
}